import React, { useCallback, useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Check, ChevronRight, Copy, Globe, MoreVertical, Share } from 'lucide-react';
import BackgroundBlobs from './BackgroundBlobs';
import {
  attemptOpenInDefaultBrowser,
  canAttemptAutoOpen,
  dismissForSession,
  getCurrentUrl,
} from '../lib/inAppBrowser';

/**
 * Full-screen modal shown before login when Harday is opened inside a social
 * in-app browser (Instagram / Facebook / Messenger / Telegram / …).
 *
 * It explains that Google Login works best in the phone's default browser and
 * offers an "Open in Browser" action:
 *   - Android: fires an Android VIEW intent to jump to the default browser.
 *     If the host app blocks it, we reveal manual "⋮ → Open in Browser" steps.
 *   - iOS: browsers can't be forced, so we show Share/Menu → Open in Default
 *     Browser instructions and copy the link as a convenience.
 *
 * Design intentionally mirrors the Login screen (gradient wordmark, glass
 * panel, DM Sans / Outfit) so it feels native to Harday. This component does
 * not touch auth, routing, referrals, tickets or rewards.
 */
const OpenInBrowserModal = ({ isOpen, appName = 'this app', platform = 'other', onClose }) => {
  const isAndroid = platform === 'android';
  const isIos = platform === 'ios';
  const canAutoOpen = isAndroid && canAttemptAutoOpen(platform);

  const [copied, setCopied] = useState(false);
  // On Android we only surface the manual "⋮ → Open in Browser" steps after an
  // auto-open attempt appears to have been blocked (page never went hidden).
  const [showManualSteps, setShowManualSteps] = useState(!canAutoOpen);
  const fallbackTimer = useRef(null);

  const clearFallbackTimer = useCallback(() => {
    if (fallbackTimer.current) {
      clearTimeout(fallbackTimer.current);
      fallbackTimer.current = null;
    }
  }, []);

  // If the page becomes hidden the intent worked (we're leaving the WebView),
  // so cancel the "it was blocked" fallback.
  useEffect(() => {
    if (!isOpen) return undefined;
    const handleHidden = () => {
      if (document.visibilityState === 'hidden') clearFallbackTimer();
    };
    document.addEventListener('visibilitychange', handleHidden);
    window.addEventListener('pagehide', clearFallbackTimer);
    window.addEventListener('blur', clearFallbackTimer);
    return () => {
      document.removeEventListener('visibilitychange', handleHidden);
      window.removeEventListener('pagehide', clearFallbackTimer);
      window.removeEventListener('blur', clearFallbackTimer);
      clearFallbackTimer();
    };
  }, [isOpen, clearFallbackTimer]);

  const handleCopyLink = useCallback(async () => {
    const url = getCurrentUrl();
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(url);
      } else {
        // Legacy fallback for older in-app WebViews without the async clipboard.
        const el = document.createElement('textarea');
        el.value = url;
        el.setAttribute('readonly', '');
        el.style.position = 'absolute';
        el.style.left = '-9999px';
        document.body.appendChild(el);
        el.select();
        document.execCommand('copy');
        document.body.removeChild(el);
      }
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      /* Clipboard blocked — the manual instructions still guide the user. */
    }
  }, []);

  const handleOpenInBrowser = useCallback(() => {
    if (canAutoOpen) {
      const fired = attemptOpenInDefaultBrowser();
      if (fired) {
        // If we're still here after a beat, the host app swallowed the intent.
        clearFallbackTimer();
        fallbackTimer.current = setTimeout(() => {
          setShowManualSteps(true);
        }, 1500);
        return;
      }
      // Couldn't even fire — go straight to manual steps.
      setShowManualSteps(true);
      return;
    }
    // iOS / other: can't force a browser. Copy the link and show the steps.
    setShowManualSteps(true);
    handleCopyLink();
  }, [canAutoOpen, clearFallbackTimer, handleCopyLink]);

  const handleContinueAnyway = useCallback(() => {
    dismissForSession();
    onClose?.();
  }, [onClose]);

  const displayAppName = appName === 'this app' ? 'this app' : `${appName}'s browser`;

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          data-testid="open-in-browser-modal"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          className="fixed inset-0 z-[100] w-full overflow-y-auto bg-white"
          style={{ minHeight: '100vh' }}
          role="dialog"
          aria-modal="true"
          aria-labelledby="open-in-browser-title"
        >
          <BackgroundBlobs />

          <div className="relative z-10 mx-auto flex min-h-screen w-full max-w-md flex-col items-center justify-center p-6">
            <motion.div
              className="w-full"
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ type: 'spring', stiffness: 300, damping: 24 }}
            >
              <div className="mb-8 text-center">
                <h1 className="mb-2 font-['Outfit'] text-5xl font-bold text-gradient">Harday</h1>
                <p className="font-['DM_Sans'] text-sm font-medium text-[#666666]">
                  One legend every day.
                </p>
              </div>

              <div className="glass-panel space-y-5 rounded-[24px] p-6">
                <div className="flex justify-center">
                  <div className="flex h-16 w-16 items-center justify-center rounded-full bg-gradient-to-r from-[#9333EA] via-[#EC4899] to-[#F59E0B]">
                    <Globe className="h-8 w-8 text-white" strokeWidth={2.2} />
                  </div>
                </div>

                <div className="text-center">
                  <h2
                    id="open-in-browser-title"
                    className="mb-2 font-['Outfit'] text-2xl font-bold text-[#000000]"
                  >
                    Open in your browser
                  </h2>
                  <p className="font-['DM_Sans'] text-sm leading-relaxed text-[#666666]">
                    Google Login works best in your phone&apos;s default browser. You&apos;re currently
                    inside {displayAppName}, which can block Google sign-in.
                  </p>
                </div>

                <motion.button
                  type="button"
                  onClick={handleOpenInBrowser}
                  data-testid="open-in-browser-btn"
                  className="flex w-full items-center justify-center gap-2 rounded-full bg-gradient-to-r from-[#9333EA] via-[#EC4899] to-[#7C3AED] py-3.5 font-semibold text-white"
                  whileHover={{ scale: 1.01 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <Globe className="h-5 w-5" />
                  <span className="font-['DM_Sans']">Open in Browser</span>
                </motion.button>

                {/* Platform-specific manual instructions */}
                <AnimatePresence initial={false}>
                  {showManualSteps && (
                    <motion.div
                      key="manual-steps"
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      exit={{ opacity: 0, height: 0 }}
                      transition={{ duration: 0.2 }}
                      data-testid="open-in-browser-instructions"
                      className="overflow-hidden"
                    >
                      <div className="rounded-[16px] border border-[#9333EA]/15 bg-white/70 p-4">
                        {isAndroid ? (
                          <p className="flex items-start gap-2 font-['DM_Sans'] text-sm text-[#374151]">
                            <MoreVertical className="mt-0.5 h-4 w-4 shrink-0 text-[#9333EA]" />
                            <span>
                              If nothing opened, tap the{' '}
                              <strong className="font-semibold text-[#000000]">⋮ menu (top-right)</strong>{' '}
                              → <strong className="font-semibold text-[#000000]">Open in Browser</strong>.
                            </span>
                          </p>
                        ) : isIos ? (
                          <p className="flex items-start gap-2 font-['DM_Sans'] text-sm text-[#374151]">
                            <Share className="mt-0.5 h-4 w-4 shrink-0 text-[#9333EA]" />
                            <span>
                              Tap the{' '}
                              <strong className="font-semibold text-[#000000]">Share / Menu (⋯)</strong>{' '}
                              button →{' '}
                              <strong className="font-semibold text-[#000000]">Open in Default Browser</strong>
                              . We&apos;ve copied the link for you.
                            </span>
                          </p>
                        ) : (
                          <p className="flex items-start gap-2 font-['DM_Sans'] text-sm text-[#374151]">
                            <ChevronRight className="mt-0.5 h-4 w-4 shrink-0 text-[#9333EA]" />
                            <span>Open this page in Chrome, Safari, or your usual browser to continue.</span>
                          </p>
                        )}

                        <button
                          type="button"
                          onClick={handleCopyLink}
                          data-testid="open-in-browser-copy"
                          className="mt-3 flex w-full items-center justify-center gap-2 rounded-full border border-gray-200 bg-white py-2.5 font-['DM_Sans'] text-sm font-semibold text-[#374151] transition-colors hover:bg-gray-50"
                        >
                          {copied ? (
                            <>
                              <Check className="h-4 w-4 text-[#16A34A]" />
                              Link copied
                            </>
                          ) : (
                            <>
                              <Copy className="h-4 w-4" />
                              Copy link
                            </>
                          )}
                        </button>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>

                <button
                  type="button"
                  onClick={handleContinueAnyway}
                  data-testid="open-in-browser-continue"
                  className="w-full py-1 font-['DM_Sans'] text-xs font-medium text-[#9CA3AF] transition-colors hover:text-[#666666]"
                >
                  Continue here anyway
                </button>
              </div>
            </motion.div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default OpenInBrowserModal;
